
























































import Vue from "vue";
import { Component, Prop, Ref } from "vue-property-decorator";

import SettingsUpdateDialog from "./SettingsUpdateDialog.vue";
import MappingCreateDialog from "./MappingCreateDialog.vue";

import { VCard, VDivider } from "vuetify/lib";
import {
  ContentField,
  ContentWarning,
  ContentLink,
  DatatableSection,
  ContentDeleteIcon,
  SectionOverview
} from "sitewhere-ide-components";

import {
  ICommandDestinationGenericConfiguration,
  IRouterGenericConfiguration,
  IDeviceTypeMappingRouterConfiguration,
  IDeviceTypeMapping
} from "sitewhere-configuration-model";

import {
  IDeviceType,
  IDeviceTypeSearchCriteria,
  IDeviceTypeResponseFormat,
  IDeviceTypeSearchResults
} from "sitewhere-rest-api";
import { AxiosResponse } from "axios";
import { showError, listDeviceTypes } from "sitewhere-ide-common";

/** Mapping for displaying data */
interface IDisplayedMapping {
  deviceTypeToken: string;
  deviceTypeName: string;
  destinationId: string;
}

@Component({
  components: {
    VCard,
    VDivider,
    ContentField,
    ContentWarning,
    ContentLink,
    DatatableSection,
    ContentDeleteIcon,
    SectionOverview,
    SettingsUpdateDialog,
    MappingCreateDialog
  }
})
export default class DeviceTypeMappingRouterSummary extends Vue {
  @Prop() readonly destinations!: ICommandDestinationGenericConfiguration[];
  @Prop() readonly router!: IRouterGenericConfiguration;
  @Ref() readonly settings!: SettingsUpdateDialog;
  @Ref() readonly mapping!: MappingCreateDialog;

  deviceTypes: IDeviceType[] = [];

  /** Table headers */
  headers: { text: string; value: string }[] = [
    { text: "Device Type", value: "type" },
    { text: "Command Destination", value: "destination" },
    { text: "", value: "delete" }
  ];

  /** Called when component is created */
  created(): void {
    this.loadDeviceTypes();
  }

  /** Get device type mapping router configuration */
  get configuration(): IDeviceTypeMappingRouterConfiguration | null {
    return this.router
      ? (this.router.configuration as IDeviceTypeMappingRouterConfiguration)
      : null;
  }

  /** Indicates if there are mappings */
  get hasMappings(): boolean {
    return this.mappings ? this.mappings.length > 0 : false;
  }

  /** Current list of device type mappings */
  get mappings(): IDeviceTypeMapping[] | null {
    return this.configuration ? this.configuration.mappings : null;
  }

  /** Get default destination */
  get defaultDestination(): string | null {
    return this.configuration ? this.configuration.defaultDestination : null;
  }

  /** Create sorted list of displayed mappings */
  get displayedMappings(): IDisplayedMapping[] {
    const mappings: IDisplayedMapping[] = [];
    if (this.configuration) {
      this.configuration.mappings.forEach(mapping => {
        const type: IDeviceType | null = this.getDeviceTypeByToken(
          mapping.deviceTypeToken
        );
        const display: IDisplayedMapping = {
          deviceTypeToken: mapping.deviceTypeToken,
          deviceTypeName: type ? type.name : mapping.deviceTypeToken,
          destinationId: mapping.destinationId
        };
        mappings.push(display);
      });
    }

    mappings.sort((a, b) => a.deviceTypeName.localeCompare(b.deviceTypeName));
    return mappings;
  }

  /** Add a new device type mapping */
  onAddMapping(): void {
    this.mapping.openDialog();
  }

  /** Called after a new mapping is added */
  onMappingAdded(mapping: IDeviceTypeMapping): void {
    if (this.configuration) {
      this.configuration.mappings.push(mapping);
      this.$emit("update");
    }
  }

  /** Get mapping index */
  getMappingIndex(
    deviceTypeToken: string,
    destinationId: string
  ): number | null {
    let match: number | null = null;
    if (this.mappings) {
      this.mappings.forEach((mapping, index) => {
        if (
          mapping.deviceTypeToken === deviceTypeToken &&
          mapping.destinationId === destinationId
        ) {
          match = index;
        }
      });
    }
    return match;
  }

  /** Called to remove an existing mapping */
  onDeleteMapping(deviceTypeToken: string, destinationId: string): void {
    const index: number | null = this.getMappingIndex(
      deviceTypeToken,
      destinationId
    );
    if (this.mappings && index != null) {
      this.mappings.splice(index, 1);
    }
    this.$emit("update");
  }

  /** Update command router settings */
  onUpdateSettings(): void {
    this.settings.openDialog(this.router.configuration);
  }

  /** Called after router settings have been updated. */
  onSettingsUpdated(updated: IDeviceTypeMappingRouterConfiguration): void {
    this.router.configuration = updated;
    this.$emit("update");
  }

  /** Load device types asynchronously */
  async loadDeviceTypes() {
    try {
      const criteria: IDeviceTypeSearchCriteria = {
        pageNumber: 1,
        pageSize: 0
      };
      const format: IDeviceTypeResponseFormat = {};
      const response: AxiosResponse<IDeviceTypeSearchResults> = await listDeviceTypes(
        this.$store,
        criteria,
        format
      );
      this.deviceTypes = response.data.results;
    } catch (err) {
      showError(this, err);
    }
  }

  /** Get a device type by token */
  getDeviceTypeByToken(token: string): IDeviceType | null {
    let match: IDeviceType | null = null;
    this.deviceTypes.forEach(deviceType => {
      if (deviceType.token === token) {
        match = deviceType;
      }
    });
    return match;
  }
}
